import type {
  WebsitePublication,
  WebsitePublicationImage,
  WebsitePublicationSourceField,
} from '@/api/core/website-publication';
import { isApiError } from '@/api/http/errors';
import { InlineLoading } from '@/components/inline-loading';
import { ZoomableImg } from '@/components/zoomable-img';
import { Button, DetailPage, DetailRow } from '@mgrvtechgroup/admin-ds';
import { useState } from 'react';
import { PHOTO_PREVIEW_LIMIT, PhotoToggle } from './photo-toggle';

function businessValue<T>(field: WebsitePublicationSourceField<T>, present: (value: T) => string) {
  if (field.state === 'OBSERVED') return present(field.value as T) || '내용 없음';
  if (field.state === 'QUARANTINED') return '확인할 수 없습니다';
  if (field.state === 'EMPTY') return '내용 없음';
  if (field.state === 'NULL') return '등록되지 않음';
  return '정보 없음';
}

function imageLabel(item: WebsitePublicationImage, index: number, kind: '외관 사진' | '상세 사진') {
  const location = item.content_slot
    ? businessValue(item.content_slot, (value) => value.trim() || '위치 정보 없음')
    : null;
  return `${kind} ${String(index + 1)}${location ? ` · ${location}` : ''}`;
}

function activeLabel(item: WebsitePublicationImage) {
  return businessValue(item.is_active, (active) => (active ? '사용 중' : '미사용'));
}

function imageList(items: WebsitePublicationImage[]) {
  return [...items]
    .map((item, index) => ({ item, index }))
    .sort(
      (left, right) =>
        left.item.display_order - right.item.display_order || left.index - right.index,
    )
    .map(({ item }) => item);
}

function PublicationImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <div
      role="status"
      className="bg-muted body text-muted-foreground flex aspect-[4/3] items-center justify-center px-3 text-center"
    >
      사진을 불러오지 못했습니다.
    </div>
  ) : (
    <ZoomableImg
      buttonClassName="w-full"
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="aspect-[4/3] w-full object-cover"
      onError={() => {
        setFailed(true);
      }}
    />
  );
}

function PublicationImages({
  title,
  items,
  kind,
}: {
  title: string;
  items: WebsitePublicationImage[];
  kind: '외관 사진' | '상세 사진';
}) {
  const ordered = imageList(items);
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? ordered : ordered.slice(0, PHOTO_PREVIEW_LIMIT);
  return (
    <section aria-label={title} className="flex min-w-0 flex-col gap-3 border-t pt-6">
      <div className="flex items-baseline gap-2">
        <h3 className="body-strong">{title}</h3>
        {ordered.length > 0 && (
          <span className="caption text-muted-foreground">{String(ordered.length)}장</span>
        )}
      </div>
      {ordered.length === 0 ? (
        <p className="body text-muted-foreground">등록된 사진이 없습니다.</p>
      ) : (
        <ul className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((item, index) => {
            const label = imageLabel(item, index, kind);
            const imageUri = item.renderable && item.value ? item.value : null;
            return (
              <li
                key={`${String(item.display_order)}-${String(index)}-${imageUri ?? 'unavailable'}`}
                className="min-w-0 overflow-hidden rounded-md border"
              >
                {imageUri ? (
                  <PublicationImage src={imageUri} alt={label} />
                ) : (
                  <div className="bg-muted body text-muted-foreground flex aspect-[4/3] items-center justify-center px-3 text-center">
                    {item.state === 'QUARANTINED'
                      ? '사진 정보를 확인할 수 없습니다.'
                      : '사진을 표시할 수 없습니다.'}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                  <span className="body">{label}</span>
                  <span className="caption text-muted-foreground">{activeLabel(item)}</span>
                  <span className="caption text-muted-foreground">
                    표시 순서 {item.display_order}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <PhotoToggle
        subject={title}
        total={ordered.length}
        expanded={expanded}
        onExpand={() => {
          setExpanded(true);
        }}
      />
    </section>
  );
}

function errorMessage(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.status === 403) return '웹 콘텐츠를 확인할 권한이 없습니다.';
    if (error.status === 404) return '웹 콘텐츠를 찾을 수 없습니다.';
    if (error.status === 409) return '지점 정보가 변경되어 웹 콘텐츠를 다시 확인해 주세요.';
    if (error.status === 503)
      return '웹 콘텐츠를 지금 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.';
    if (error.kind === 'invalid-response') return '웹 콘텐츠를 확인할 수 없습니다.';
  }
  return '웹 콘텐츠를 불러오지 못했습니다.';
}

export function WebsitePublicationSection({
  data,
  isFetching,
  isError,
  error,
  onRefresh,
}: {
  data: WebsitePublication | undefined;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  onRefresh: () => void;
}) {
  return (
    <DetailPage.Section
      aria-label="웹 콘텐츠"
      title="웹 콘텐츠"
      description="게시 설정을 확인합니다."
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={onRefresh}
          loading={isFetching}
          focusableWhenDisabled
        >
          다시 조회
        </Button>
      }
    >
      {isFetching ? (
        <InlineLoading>
          웹 콘텐츠를 불러오고 있습니다. 응답에 시간이 걸릴 수 있습니다.
        </InlineLoading>
      ) : isError || !data ? (
        <p role="alert" className="body text-warning-foreground" aria-live="polite">
          {errorMessage(error)}
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <DetailRow
              label="게시 설정"
              value={businessValue(data.publication.is_published, (published) =>
                published ? '게시하도록 설정됨' : '게시하지 않도록 설정됨',
              )}
            />
            <DetailRow
              label="한국어 소개"
              value={
                <span className="[overflow-wrap:anywhere] break-keep whitespace-pre-wrap">
                  {businessValue(data.publication.tagline.ko, String)}
                </span>
              }
            />
            <DetailRow
              label="영어 소개"
              value={
                <span className="[overflow-wrap:anywhere] whitespace-pre-wrap">
                  {businessValue(data.publication.tagline.en, String)}
                </span>
              }
            />
          </div>
          <PublicationImages title="외관 사진" items={data.images.exterior} kind="외관 사진" />
          <PublicationImages title="상세 사진" items={data.images.detail} kind="상세 사진" />
        </div>
      )}
    </DetailPage.Section>
  );
}
